/**
 * @file Spotlight.tsx
 * @description Subtle animated spotlight background effect
 *
 * Based on Aceternity UI's Spotlight component
 * Creates animated radial gradients that move across the background
 *
 * @see https://ui.aceternity.com/components/spotlight-new
 */

import type { ReactElement } from "react";
interface SpotlightProps {
  /** First gradient color for the spotlight effect */
  readonly gradientFirst?: string;
  /** Second gradient color for the spotlight effect */
  readonly gradientSecond?: string;
  /** Third gradient color for the spotlight effect */
  readonly gradientThird?: string;
  /** Vertical translation offset in pixels */
  readonly translateY?: number;
  /** Width of the main spotlight element in pixels */
  readonly width?: number;
  /** Height of the spotlight elements in pixels */
  readonly height?: number;
  /** Width of the smaller spotlight elements in pixels */
  readonly smallWidth?: number;
  /** Animation duration in seconds */
  readonly duration?: number;
  /** Horizontal animation offset in pixels */
  readonly xOffset?: number;
  /** Brightness multiplier for the spotlight effect (0.5 = dimmer, 2 = brighter) */
  readonly brightness?: number;
}

/**
 * Multiplies alpha values in an HSLA gradient string by a brightness factor
 */
function adjustGradientBrightness(gradient: string, brightness: number): string {
  // Using replace with global flag instead of replaceAll for better compatibility
  return gradient.replace(
    /hsla\((\d+),\s*(\d+)%,\s*(\d+)%,\s*([\d.]+)\)/g,
    (_match: string, h: string, s: string, l: string, a: string) => {
      const newAlpha = Math.min(1, Number.parseFloat(a) * brightness);
      return `hsla(${h}, ${s}%, ${l}%, ${newAlpha})`;
    },
  );
}

export function Spotlight({
  gradientFirst = "radial-gradient(50% 50% at 20% 50%, hsla(180, 100%, 75%, .12) 0, hsla(210, 100%, 65%, .03) 30%, hsla(160, 100%, 70%, .01) 60%, transparent 100%)",
  gradientSecond = "radial-gradient(50% 50% at 50% 50%, hsla(200, 100%, 70%, .10) 0, hsla(170, 100%, 65%, .03) 50%, transparent 100%)",
  gradientThird = "radial-gradient(50% 50% at 50% 50%, hsla(220, 100%, 75%, .08) 0, hsla(190, 100%, 70%, .02) 60%, transparent 100%)",
  translateY = -1920,
  width = 1920,
  height = 3840,
  smallWidth = 720,
  duration = 22,
  xOffset = 100,
  brightness = 1.15,
}: SpotlightProps): ReactElement {
  // Apply brightness multiplier to gradients
  const adjustedGradientFirst = adjustGradientBrightness(gradientFirst, brightness);
  const adjustedGradientSecond = adjustGradientBrightness(gradientSecond, brightness);
  const adjustedGradientThird = adjustGradientBrightness(gradientThird, brightness);
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {/* Main spotlight - left side */}
      <div
        className="absolute left-0 top-0 will-change-transform"
        style={{
          width: `${width}px`,
          height: `${height}px`,
          transform: `translate(0px, ${translateY}px)`,
          background: adjustedGradientFirst,
          animation: `spotlight-left ${duration}s ease-in-out infinite`,
        }}
      />

      {/* Secondary spotlight - right side */}
      <div
        className="absolute right-0 top-0 will-change-transform"
        style={{
          width: `${smallWidth}px`,
          height: `${height}px`,
          transform: `translate(0px, ${translateY}px)`,
          background: adjustedGradientSecond,
          animation: `spotlight-right ${duration}s ease-in-out infinite`,
          animationDelay: `${duration / 3}s`,
        }}
      />

      {/* Tertiary spotlight - center */}
      <div
        className="absolute left-1/2 top-0 -translate-x-1/2 will-change-transform"
        style={{
          width: `${smallWidth}px`,
          height: `${height}px`,
          transform: `translate(-50%, ${translateY}px)`,
          background: adjustedGradientThird,
          animation: `spotlight-center ${duration}s ease-in-out infinite`,
          animationDelay: `${duration / 6}s`,
        }}
      />

      <style>{`
                @keyframes spotlight-left {
                    0%, 100% {
                        transform: translate(0px, ${translateY}px);
                    }
                    33% {
                        transform: translate(${xOffset * 0.6}px, ${translateY - 30}px);
                    }
                    66% {
                        transform: translate(${xOffset}px, ${translateY + 20}px);
                    }
                }

                @keyframes spotlight-right {
                    0%, 100% {
                        transform: translate(0px, ${translateY}px);
                    }
                    33% {
                        transform: translate(-${xOffset * 0.5}px, ${translateY + 40}px);
                    }
                    66% {
                        transform: translate(-${xOffset}px, ${translateY - 20}px);
                    }
                }

                @keyframes spotlight-center {
                    0%, 100% {
                        transform: translate(-50%, ${translateY}px);
                    }
                    25% {
                        transform: translate(-50%, ${translateY + 60}px);
                    }
                    50% {
                        transform: translate(-45%, ${translateY + 30}px);
                    }
                    75% {
                        transform: translate(-55%, ${translateY + 50}px);
                    }
                }
            `}</style>
    </div>
  );
}
